import type {IPanelSidebarMenuItem} from '../../components/panel-sections/sidebar';
import type {CommandSearchScope} from '../../components/panel-command-search';
import {toScopePath} from '../../components/panel-command-search';
import {resolvePanelEntryTarget, walkPanelEntriesForSidebar} from '../access';
import type {PanelEntry, PanelEntryContext} from '../types';

const resolveSidebarPath = (
    entry: PanelEntry,
    ctx: PanelEntryContext,
): string | string[] | undefined => {
    const path = entry.sidebar?.path;
    if (typeof path === 'function') {
        return path(ctx);
    }
    if (path !== undefined) {
        return path;
    }
    return entry.to ?? entry.routeTemplate;
};

const toSidebarItem = (entry: PanelEntry, ctx: PanelEntryContext): IPanelSidebarMenuItem | null => {
    if (entry.children?.length) {
        const children = entry.children
            .map((child) => toSidebarItem(child, ctx))
            .filter((item): item is IPanelSidebarMenuItem => item != null);

        if (!children.length && !resolvePanelEntryTarget(entry, ctx) && !entry.sidebar?.path) {
            return null;
        }

        return {
            key: entry.id,
            primaryText: entry.sidebarTitle ?? entry.title,
            icon: entry.icon,
            LinkProps: {
                path: resolveSidebarPath(entry, ctx),
                to: resolvePanelEntryTarget(entry, ctx),
            },
            ...(entry.sidebar?.ListItemIconProps ? {ListItemIconProps: entry.sidebar.ListItemIconProps} : {}),
            ...(entry.sidebar?.badge !== undefined ? {badge: entry.sidebar.badge} : {}),
            children,
        } as IPanelSidebarMenuItem;
    }

    const to = resolvePanelEntryTarget(entry, ctx);
    if (!to && !entry.sidebar?.path) {
        return null;
    }

    return {
        key: entry.id,
        primaryText: entry.sidebarTitle ?? entry.title,
        icon: entry.icon,
        LinkProps: {
            to,
            path: resolveSidebarPath(entry, ctx),
        },
        ...(entry.sidebar?.ListItemIconProps ? {ListItemIconProps: entry.sidebar.ListItemIconProps} : {}),
        ...(entry.sidebar?.badge !== undefined ? {badge: entry.sidebar.badge} : {}),
    } as IPanelSidebarMenuItem;
};

/**
 * Converts a panel-entry tree into nested sidebar menu items.
 * Pass the operator or customer catalog roots (not the merged list with actions).
 */
export const panelEntriesToSidebarItems = (
    entries: PanelEntry[],
    scope: CommandSearchScope,
    services?: Record<string, unknown>,
): IPanelSidebarMenuItem[] => {
    const ctx: PanelEntryContext = {
        scope,
        scopePath: toScopePath(scope),
        services: {
            ...services,
            panelEntries: entries,
        },
    };

    return walkPanelEntriesForSidebar(entries, ctx)
        .map((entry) => toSidebarItem(entry, ctx))
        .filter((item): item is IPanelSidebarMenuItem => item != null);
};
